import joi from 'joi';

import type { GraphPath } from './GraphPath';
import {
  type GraphPathLookupParameters,
  graphPathLookupParametersSchema,
} from './api/GraphPathLookupParameters';
import {
  type GraphTopologyParameters,
  graphTopologyParametersSchema,
} from './api/GraphTopologyParameters';
import type { GraphOverlay } from './graphOverlay';
import type { PathLookupGraphSettings } from './graphSettings/PathLookupGraphPreset';
import type { TopologyGraphSettings } from './graphSettings/TopologyGraphPreset';
import {
  type CollapsedNodeGroup,
  type NodePositions,
  type NodeSn,
  collapsedNodeGroupSchema,
  nodePositionsSchema,
} from './nodes/types';
import { brand } from './types/Branded';

export const IPF_NETWORK_VIEWER = brand<GraphPath>('$main');
export const IPF_NETWORK_VIEWER_LABEL = 'All network';
export const IPF_NO_VALUE_CLOUD = brand<GraphPath>('$noValue');
export const IPF_NO_VALUE_CLOUD_LABEL = '<NO VALUE>';

export type GraphViewId = string;
export type GraphUrlId = string;

export interface WithPreview {
  preview: string;
}

export interface WithUserDetails {
  userId: string;
  username: string;
}

export interface GraphViewBase {
  collapsedNodeGroups: CollapsedNodeGroup[];
  hiddenNodes: NodeSn[];
  name: string;
  parameters: GraphTopologyParameters | GraphPathLookupParameters;
  positions: NodePositions;
}

export interface GraphView extends GraphViewBase, WithUserDetails {
  id: GraphViewId;
  favorite?: boolean;
  hidden?: boolean;
  overlay?: GraphOverlay;
  savedAt: number;
  settings: TopologyGraphSettings | PathLookupGraphSettings;
}

export type GraphViewPreviewable = GraphView & WithPreview;

export const pathsContainNetworkViewer = (paths: GraphPath[]): boolean =>
  paths.includes(IPF_NETWORK_VIEWER);

export const pathsContainNoValueCloud = (paths: GraphPath[]): boolean =>
  paths.includes(IPF_NO_VALUE_CLOUD);

export const graphTopologyParametersWithoutLayoutSchema =
  graphTopologyParametersSchema.keys({
    layouts: joi.forbidden(),
  });

export const graphViewBaseSchema = joi.object().keys({
  collapsedNodeGroups: joi.array().items(collapsedNodeGroupSchema).required(),
  hiddenNodes: joi.array().items(joi.string()).required(), // explicitly hidden nodes
  name: joi.string().required(),
  parameters: joi
    .alternatives([
      graphTopologyParametersWithoutLayoutSchema,
      graphPathLookupParametersSchema,
    ])
    .required(),
  positions: nodePositionsSchema.required(),
});
